import { Landmark, Globe2, Receipt, ShieldCheck, ShieldOff, Sparkles } from "lucide-react";
import { taxData, usTaxLayer } from "@/data/taxes";

const Fact = ({ icon: Icon, label, text, testid }) => (
  <div className="bg-blush rounded-xl p-5 border border-petal/20" data-testid={testid}>
    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal mb-2 flex items-center gap-1.5"><Icon size={12} /> {label}</p>
    <p className="text-sm text-ink/85 leading-relaxed">{text}</p>
  </div>
);

export const TaxesTab = ({ country }) => {
  const t = taxData[country.slug];
  if (!t) return null;
  return (
    <div data-testid="taxes-tab">
      <p className="text-base text-ink/85 leading-relaxed max-w-3xl mb-6">
        What {country.name} expects from you once you're a resident — and what Uncle Sam still expects no matter where you pray Fajr. General guidance from one mom's research; confirm with a cross-border accountant before you file.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Fact icon={Landmark} label="When you become a tax resident" text={t.residency} testid="tax-residency" />
        <Fact icon={Receipt} label="Local income tax" text={t.incomeTax} testid="tax-income" />
        <Fact icon={Globe2} label="Your US / foreign income" text={t.foreignIncome} testid="tax-foreign" />
        <Fact icon={Receipt} label="VAT & everyday taxes" text={t.consumption} testid="tax-consumption" />
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-5 border border-pine/15" data-testid="tax-other">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-pine mb-3">Also worth knowing</p>
          <ul className="space-y-2">
            {t.other.map((o) => <li key={o} className="text-sm text-ink/85 leading-relaxed flex gap-2"><span className="text-petal">·</span>{o}</li>)}
          </ul>
        </div>
        <div className={`rounded-xl p-5 border ${t.usTreaty ? "bg-pine/5 border-pine/20" : "bg-white border-pine/15"}`} data-testid="tax-treaty">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-pine mb-3 flex items-center gap-1.5">
            {t.usTreaty ? <ShieldCheck size={12} /> : <ShieldOff size={12} />} US tax treaty: {t.usTreaty ? "yes" : "none"}
          </p>
          <p className="text-sm text-ink/85 leading-relaxed">{t.treatyNote}</p>
          <div className="mt-4 bg-blush rounded-lg p-4 border border-petal/25">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal flex items-center gap-1.5 mb-1.5"><Sparkles size={12} /> Mom's read</p>
            <p className="font-serif italic text-ink/85 leading-relaxed text-[15px]">{t.momNote}</p>
          </div>
        </div>
      </div>
      <div className="mt-6 bg-pine-ink text-cream rounded-2xl p-6 lg:p-7 grain relative" data-testid="tax-us-layer">
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal-soft mb-2">The American layer</p>
        <h3 className="font-serif font-semibold text-xl lg:text-2xl">{usTaxLayer.headline}</h3>
        <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5">
          {usTaxLayer.points.map((p) => <li key={p} className="text-sm text-cream/90 leading-relaxed flex gap-2"><span className="text-petal-soft">✦</span>{p}</li>)}
        </ul>
        <a
          href="https://www.irs.gov/individuals/international-taxpayers/us-citizens-and-resident-aliens-abroad"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="tax-irs-link"
          className="mt-5 inline-flex items-center gap-2 text-xs font-semibold text-petal-soft hover:text-cream transition-colors underline underline-offset-4"
        >
          IRS: U.S. Citizens and Resident Aliens Abroad →
        </a>
      </div>
    </div>
  );
};
